import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import type { PbjLockedAction, PbjPrediction, PbjSuggestedPlan } from "../../../utils/models";
import { usePbjSuggestedPlan } from "../hooks/usePbjPrediction";
import { stepPricedSpend } from "./recMath.utils";
import type { PlanItem, RecommendationType } from "./recommendations.constants";
import { cutStepsSummary } from "./signedCopy.utils";

type Args = {
	prediction: PbjPrediction;
	census: number;
	selectedPlan: PlanItem[];
	// Committed ISO date range — the window the suggested-plan endpoint re-optimizes over.
	startDate: string;
	endDate: string;
	visibleRecs: RecommendationType[];
	// Positions the non-locked sliders from the plan that came back.
	onApply: (plan: PbjSuggestedPlan, lockedIds: Set<string>) => void;
};

/**
 * "Suggest plan" ONLY moves sliders — it never mutates the Action Plan. When the plan has
 * manually-added buckets they go up as lockedActions so the BE re-optimizes the OTHER buckets around
 * them; with an empty plan the prediction's pre-computed plan is reused (no round-trip).
 */
export const useSuggestPlanClick = ({ prediction, census, selectedPlan, startDate, endDate, visibleRecs, onApply }: Args) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { showErrorSnackbar } = useHospitalData();
	const suggestMutation = usePbjSuggestedPlan();

	const handleSuggestClick = useCallback(async () => {
		const lockedActions: PbjLockedAction[] = selectedPlan.map((p) => ({
			actionId: p.id,
			// DEFAULT: an item stored without a breakdown moves no measure the optimizer can see, so it locks
			// with no category gaps — the same 0 the cumulative pass scores it at — rather than failing Suggest.
			categoryGaps: Object.fromEntries((p.breakdown ?? []).map((b) => [b.categoryId, b.hppdGap])),
		}));
		// `mutateAsync` rejects on any 4xx/5xx. Without this catch the rejection is unhandled and the
		// click silently does nothing — the BE rejects an unrecognized locked action id with a translated
		// `detail.message`. Sliders are deliberately left untouched on failure: a partial re-position
		// built from a plan we never received would misrepresent the suggestion.
		let plan: PbjSuggestedPlan;
		try {
			plan =
				lockedActions.length === 0
					? prediction.suggestedPlan
					: await suggestMutation.mutateAsync({ departmentId: prediction.config.departmentId, startDate, endDate, lockedActions });
		} catch (error) {
			// `as` narrows axios' untyped error payload to the one field the BE contract defines.
			const messageKey = (error as { response?: { data?: { detail?: { message?: string } } } })?.response?.data?.detail?.message;
			// The backend's translated reason when it sent one, else the generic "couldn't suggest" copy.
			const shownKey = messageKey ? messageKey : "pbj_model.suggest_plan.failed";
			track("pbj_model_suggest_plan_error", { department_id: prediction.config.departmentId, locked_count: lockedActions.length, error_key: shownKey });
			showErrorSnackbar(t(shownKey), { action: "pbj_model_suggest_plan" }, error);
			return;
		}
		// Counted from the plan actually applied — with locked actions that's the re-optimized response,
		// not the prediction's pre-computed plan — and priced like the Action Plan prices it.
		const recById = new Map<string, RecommendationType>(visibleRecs.map((r) => [r.id, r]));
		const pricedSteps = plan.steps.flatMap((step) => {
			const rec = recById.get(step.actionId);
			return rec ? [{ actionId: step.actionId, spend: step.spend, pricedSpend: stepPricedSpend(rec, step, prediction) }] : [];
		});
		const cutSteps = cutStepsSummary(pricedSteps, census, prediction.quarterDays, prediction.quarterWeekendDays);
		track("pbj_model_suggest_plan_clicked", {
			department_id: prediction.config.departmentId,
			target_stars: prediction.targetStars,
			steps_count: plan.steps.length,
			cut_steps_count: cutSteps.count,
			projected_savings: Math.round(cutSteps.savings),
			target_met: plan.targetMet,
			locked_count: lockedActions.length,
		});
		onApply(plan, new Set(lockedActions.map((l) => l.actionId)));
	}, [census, endDate, onApply, prediction, selectedPlan, showErrorSnackbar, startDate, suggestMutation, t, track, visibleRecs]);

	return { handleSuggestClick, isSuggesting: suggestMutation.isPending };
};
